/**
 * documentFile.ts — Sprint 147 (ADR-142, R14-7). The document-file contract.
 *
 * ADR-136 modelled two document states:
 *
 *   1. NO FILE      — `fileUrl` null/empty. View + Download render DISABLED
 *                     with a "No file attached" tooltip. Correct.
 *   2. SERVED FILE  — `fileUrl` is `/api/documents/files/<name>`, which
 *                     `DocumentService.resolveOwnedDocument` matches on. Works.
 *
 * Round 14 found a THIRD state nobody modelled: a row carrying a `fileUrl`
 * (plus a plausible `fileSize`) that the serve endpoint cannot resolve — e.g.
 * the seeded `/storage/birch/rules-2025.pdf`. The old `hasFile()` only asked
 * "is this string non-empty?", so View ENABLED, then 404'd with
 * "Could not open document: Endpoint not found". 6 of 8 seeded documents were
 * in that state.
 *
 * **The contract, decided in ADR-142:** a document's `fileUrl` is either
 * absent (no file) or a path the serve endpoint can resolve, i.e. it ends up
 * at `/api/documents/files/<filename>`. Nothing else is a valid stored value.
 * The seed no longer invents `/storage/*` URLs (that is the root fix); this
 * helper is the client-side enforcement of the same rule, so a non-conforming
 * row — legacy data, a hand-edited record, a future importer — degrades to the
 * honest disabled state instead of offering a button that cannot work.
 *
 * Deliberately NOT chosen: teaching the serve endpoint to resolve `/storage/*`.
 * There is no object store behind those paths in any environment; resolving
 * them would mean inventing storage, not clarifying a contract.
 */

/** The only prefix the authenticated serve endpoint can resolve. */
export const SERVED_PREFIX = '/api/documents/files/';

/** A historical placeholder that some legacy rows still carry. */
const LEGACY_PLACEHOLDER = '/documents/placeholder.pdf';

/** Reduce an absolute URL to its path; leave a path untouched. */
function toPath(fileUrl: string): string {
  if (/^https?:\/\//i.test(fileUrl)) {
    try {
      return new URL(fileUrl).pathname;
    } catch {
      return fileUrl;
    }
  }
  return fileUrl.startsWith('/') ? fileUrl : `/${fileUrl}`;
}

/**
 * True when `fileUrl` names a file the serve endpoint can actually return.
 *
 * This is what View/Download must gate on — not merely "is the string
 * non-empty". A truthy-but-unservable value is the R14-7 third state.
 */
export function hasServableFile(fileUrl: string | undefined | null): boolean {
  if (!fileUrl || !fileUrl.trim()) return false;
  if (fileUrl === LEGACY_PLACEHOLDER) return false;
  const path = toPath(fileUrl.trim());
  if (!path.startsWith(SERVED_PREFIX)) return false;
  // Must actually name a file, not just carry the prefix.
  return path.slice(SERVED_PREFIX.length).trim().length > 0;
}

/**
 * The path to hand `downloadAuthed`/`apiFetch`, which already prepend the
 * `/api` base — so the stored `/api/documents/files/<name>` is returned as
 * `/documents/files/<name>`.
 *
 * Only meaningful for a value that passes {@link hasServableFile}; callers
 * must check first.
 */
export function toServePath(fileUrl: string): string {
  const path = toPath(fileUrl);
  return path.startsWith('/api/') ? path.slice(4) : path;
}

/** Why a document's file cannot be opened — drives the tooltip copy. */
export type DocumentFileState = 'ready' | 'none' | 'unavailable';

export function documentFileState(fileUrl: string | undefined | null): DocumentFileState {
  if (hasServableFile(fileUrl)) return 'ready';
  if (!fileUrl || !fileUrl.trim() || fileUrl === LEGACY_PLACEHOLDER) return 'none';
  return 'unavailable';
}

/** Honest, state-specific tooltip copy for a disabled View/Download control. */
export function documentFileTooltip(
  fileUrl: string | undefined | null,
  action: 'view' | 'download',
): string {
  switch (documentFileState(fileUrl)) {
    case 'ready':
      return action === 'view' ? 'Open in new tab' : 'Download file';
    case 'unavailable':
      return 'File is not available on this server';
    default:
      return 'No file attached';
  }
}
